import { useRef, useEffect } from "react";
import {
  TRAVEL_STYLE_OPTIONS,
  WEATHER_OPTIONS,
  ACCOMMODATION_OPTIONS,
  FOOD_OPTIONS,
  TRAVEL_TO_OPTIONS,
  TRAVEL_BETWEEN_OPTIONS,
  MEAL_PLAN_OPTIONS,
  TRIP_TYPE_OPTIONS,
} from "./constants";
import SelectChip from "./SelectChip";
import { trackEvent } from "../../utils/analytics";

const Step2Form = ({ formData, setFormData, isInternationalTrip = false, showErrors = false }) => {
  const weatherRef = useRef(null);
  const travelBetweenRef = useRef(null);

  useEffect(() => {
    // Force 3star default if array is missing or empty (e.g. from older local storage states)
    if (!formData.accommodation || formData.accommodation.length === 0) {
      setFormData((prev) => ({ ...prev, accommodation: ["3 Star", "4 Star"] }));
    }
  }, [isInternationalTrip, formData.accommodation]);

  const toggleMulti = (field, value) => {
    const current = formData[field] || [];

    const isSelecting = !current.includes(value);

    if (isSelecting) {
      if (field === "weather") {
        trackEvent("weather_selected", {
          weather_type: value,
        });
      }

      if (field === "accommodation") {
        trackEvent("accommodation_selected", {
          accommodation_type: value,
        });
      }

      if (field === "food") {
        trackEvent("food_preference_selected", {
          food_type: value,
        });
      }
    }

    setFormData({
      ...formData,
      [field]: isSelecting
        ? [...current, value]
        : current.filter((v) => v !== value),
    });
  };

  const toggleSingle = (field, value, nextRef) => {
    if (field === "travelStyle" && formData[field] !== value) {
      trackEvent("explore_mode_selected", {
        travel_style: value,
      });
    }

    if (field === "tripType" && formData[field] !== value) {
      trackEvent("trip_type_selected", {
        trip_type: value,
      });
    }

    if (field === "mealPlan" && formData[field] !== value) {
      trackEvent("meal_plan_selected", {
        meal_plan: value,
      });
    }

    setFormData({
      ...formData,
      [field]: formData[field] === value ? "" : value,
    });

    if (nextRef?.current && formData[field] !== value) {
      setTimeout(() => {
        nextRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          How do you like to explore a destination?
          <span className="text-gray-400 font-normal ml-2">(Optional)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {TRAVEL_STYLE_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={formData.travelStyle === opt.id}
              onClick={() => toggleSingle("travelStyle", opt.id, weatherRef)}
              variant="outline"
            />
          ))}
        </div>
      </div>

      <div ref={weatherRef}>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          What kind of weather do you prefer for your trip?
          <span className="text-gray-400 font-normal ml-2">(Optional)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {WEATHER_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={(formData.weather || []).includes(opt.id)}
              onClick={() => toggleMulti("weather", opt.id)}
              variant="outline"
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Who are you traveling with? (Trip Type)
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {TRIP_TYPE_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={formData.tripType === opt.id}
              onClick={() => toggleSingle("tripType", opt.id)}
              variant="outline"
            />
          ))}
        </div>
        {showErrors && !formData.tripType && (
          <p className="mt-2 text-xs font-medium text-red-500">
            This field is missing — please select who you're traveling with.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          What type of accommodation would you prefer?
        </label>
        <div className="flex flex-wrap gap-2">
          {ACCOMMODATION_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={(formData.accommodation || []).includes(opt.id)}
              onClick={() => toggleMulti("accommodation", opt.id)}
              variant="outline"
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          What type of food would you like to enjoy during your trip?
          <span className="text-gray-400 font-normal ml-2">(Optional)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {FOOD_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={(formData.food || []).includes(opt.id)}
              onClick={() => toggleMulti("food", opt.id)}
              variant="outline"
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          What is your preferred Meal Plan?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {MEAL_PLAN_OPTIONS.map((opt) => (
            <label
              key={opt.id}
              className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${
                formData.mealPlan === opt.id
                  ? "border-teal-500 bg-teal-50 ring-1 ring-teal-500"
                  : "border-gray-200 hover:border-teal-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="mealPlan"
                value={opt.id}
                checked={formData.mealPlan === opt.id}
                onChange={() => toggleSingle("mealPlan", opt.id)}
                className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
              />
              <span className="ml-3 block text-sm font-medium text-gray-900">
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          How would you like to travel from departure to destination?
          <span className="text-red-500 ml-1">*</span>
          <span className="text-gray-400 font-normal ml-2 text-xs">
            {isInternationalTrip ? "" : "(Select multiple)"}
          </span>
        </label>
        <div className="flex flex-wrap gap-2">
          {TRAVEL_TO_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={
                isInternationalTrip
                  ? formData.travelToDest === opt.id
                  : (formData.travelToDest || []).includes(opt.id)
              }
              onClick={() => {
                const current = Array.isArray(formData.travelToDest)
                  ? formData.travelToDest
                  : [];

                if (!current.includes(opt.id)) {
                  trackEvent("departure_transport_selected", {
                    transport_mode: opt.id,
                  });
                }

                if (isInternationalTrip) {
                  toggleSingle("travelToDest", opt.id, travelBetweenRef);
                } else {
                  const current = Array.isArray(formData.travelToDest)
                    ? formData.travelToDest
                    : formData.travelToDest
                      ? [formData.travelToDest]
                      : [];

                  setFormData({
                    ...formData,
                    travelToDest: current.includes(opt.id)
                      ? current.filter((v) => v !== opt.id)
                      : [...current, opt.id],
                  });
                }
              }}
              variant="outline"
            />
          ))}
        </div>
      </div>

      <div ref={travelBetweenRef}>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          How would you prefer to travel between destinations?
          <span className="text-gray-400 font-normal ml-2">(Optional)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {TRAVEL_BETWEEN_OPTIONS.map((opt) => (
            <SelectChip
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={(formData.travelBetween || []).includes(opt.id)}
              onClick={() => {
                const current = formData.travelBetween || [];

                if (!current.includes(opt.id)) {
                  trackEvent("intercity_transport_selected", {
                    transport_mode: opt.id,
                  });
                }

                toggleMulti("travelBetween", opt.id);
              }}
              variant="outline"
            />
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <label className="flex items-center justify-between cursor-pointer group">
          <div>
            <span className="block text-sm font-semibold text-gray-900">
              Include Transport Package
            </span>
            <span className="block text-sm text-gray-500 mt-1">
              Let Romaya handle flight and transport bookings (Turn off for
              land-only package)
            </span>
          </div>
          <div className="relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus-within:ring-2 focus-within:ring-teal-500 focus-within:ring-offset-2">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={formData.includeTransport !== false}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  includeTransport: e.target.checked,
                });
                trackEvent("transport_package_toggled", {
                  enabled: e.target.checked,
                });
              }}
            />
            <div
              className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${formData.includeTransport !== false ? "bg-teal-600 peer-checked:bg-teal-600" : ""}`}
            ></div>
          </div>
        </label>
      </div>
    </div>
  );
};

export default Step2Form;
